import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useBoolean } from 'ahooks';
import { InboxCustom } from '@archipro-design/icons';
import { useStyles } from '@archipro-design/aria';
import PinOverlayButton from '@modules/design-board/component/shared/PinOverlayButton';
import cx from 'classnames';
import * as S from '@modules/design-board/component/shared/TileTopActionButtons.desktop.style';
import { useCommentDialog } from '~/modules/build-hub-comment/hooks/useCommentDialog';
import { cn } from '@rocco/utils/cn';

interface CommentButtonProps {
    itemId: number;
    commentsCount?: number;
    unreadCount?: number;
    inline?: boolean;
    onClick?: (e: React.SyntheticEvent<HTMLElement>) => void;
    className?: string;
}

export function CommentButton({
    itemId,
    commentsCount,
    unreadCount = 0,
    inline,
    onClick,
    className,
}: CommentButtonProps) {
    const { css } = useStyles();
    const [tooltipState, tooltipAction] = useBoolean(false);
    const { openDialog, commentDialog } = useCommentDialog();

    // Track previous unread count for animation triggers
    const previousUnreadCountRef = useRef<number>(unreadCount);
    const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Trigger animation when unread count increases
    useEffect(() => {
        if (unreadCount > previousUnreadCountRef.current) {
            setShouldAnimate(true);
            // Clear animation flag after duration
            animationTimeoutRef.current = setTimeout(() => {
                setShouldAnimate(false);
            }, 500);
        }
        previousUnreadCountRef.current = unreadCount;

        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [unreadCount]);

    const onClickComment = (e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();

        if (onClick) {
            onClick(e);
        } else {
            openDialog(itemId);
        }
    };

    const hasUnread = unreadCount > 0;
    const showCommentsCount = (commentsCount ?? 0) > 0;
    const displayCommentsCount =
        commentsCount && commentsCount > 9 ? '9+' : commentsCount?.toString();
    const displayUnreadCount = unreadCount > 9 ? '9+' : unreadCount;

    if (inline) {
        return (
            <>
                <button
                    onClick={onClickComment}
                    className={cn(
                        'relative flex items-center justify-center rounded-full p-1 transition-colors',
                        'hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50',
                        className
                    )}
                    aria-label="Comment"
                >
                    <div className="flex size-6 items-center justify-center">
                        <InboxCustom
                            className="mr-1 h-4 w-5"
                            style={{ width: '1.25rem', height: '1rem' }}
                        />
                    </div>
                    <div className="relative flex items-center gap-1">
                        <span
                            className={cn(
                                'text-sm font-medium leading-4 transition-colors',
                                shouldAnimate && 'font-semibold'
                            )}
                            style={
                                shouldAnimate
                                    ? {
                                          animation:
                                              'scale-pulse 0.6s ease-out',
                                      }
                                    : undefined
                            }
                        >
                            {commentsCount}
                        </span>
                    </div>
                    {hasUnread && (
                        <span className="absolute -right-1 -top-1 flex size-4.5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold leading-none text-white">
                            {displayUnreadCount}
                        </span>
                    )}
                </button>

                {commentDialog()}
            </>
        );
    }

    return (
        <>
            <div className="relative inline-block">
                <PinOverlayButton
                    className={cx(
                        css(S.CommentButton),
                        css(S.MessageToolTipStyle)
                    )}
                    open={tooltipState}
                    booleanActions={tooltipAction}
                    onClick={onClickComment}
                    icon={<InboxCustom />}
                    toolTipText={'Comment'}
                />
                {hasUnread ? (
                    <span className="absolute -right-1 -top-1 flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
                        {displayUnreadCount}
                    </span>
                ) : (
                    showCommentsCount && (
                        <span
                            className={cx(
                                'absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold text-white'
                            )}
                            style={
                                shouldAnimate
                                    ? {
                                          animation:
                                              'scale-pulse 0.5s ease-in-out',
                                      }
                                    : undefined
                            }
                        >
                            {displayCommentsCount}
                        </span>
                    )
                )}
            </div>
            {commentDialog()}
        </>
    );
}
